// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGrandTotalGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import AppGlobal from 'Izenda.AppGlobal';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { toJS } from 'mobx';
import connect from 'share/enhancers/Izenda.Connect';
import { Table } from 'share/components/controls/DataGrid/Izenda.Table';
import { Column } from 'share/components/controls/DataGrid/Izenda.Table';
import classnames from 'classnames';
import { DEFAULT_CELL_STYLE, GRID_STYLES, RECORDS_MAX_LENGTH } from 'logic/data/Izenda.ReportPartGridConstants';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import convertToNumeralFormat from 'share/utils/Izenda.ConvertToNumeralFormat';
import { isNil, take, get, isEmpty, defaults, forEach, flow, max, find, map } from 'lodash/fp';
import propTypes from 'prop-types';

@connect()
export default class ReportPartGrandTotalGrid extends Component {
  static propTypes = {
    reportPart: propTypes.object.isRequired,
    records: propTypes.oneOfType([propTypes.object, propTypes.array]).isRequired,
    reportPartElementTypes: propTypes.array
  };

  render() {
    const gridStyle = get('reportPart.reportPartContent.properties.generalInfo.gridStyle', this.props);
    const result = this.generateHeaderData();
    const className = classnames(
      'report-part-grid-preview',
      { hide: isNil(result.headers) },
      { 'vertical-grid': gridStyle === GRID_STYLES.VERTICAL.value },
      { 'horizontal-grid': gridStyle === GRID_STYLES.HORIZONTAL.value }
    );
    const records = this.props.isPreview ? take(RECORDS_MAX_LENGTH, result.records) : result.records;
    const gridWidth = get('reportPartContent.properties.columns.width.value', this.props.reportPart);

    return (
      !isEmpty(records) && (
        <section className={className}>
          <Table
            showScroll={false}
            dataSource={records}
            className="custom-table"
            autoResize={!(gridWidth > 0)}
            staticRender={this.props.staticRender}
          >
            {this.renderColumn(result.headers, result.records.length)}
          </Table>
        </section>
      )
    );
  }

  /**
   * Render column
   * @param {Array} headers 
   */
  renderColumn(headers: Array<any>) {
    const { reportPart } = this.props;
    const properties = reportPart.getReportPartProperties();
    const columns = [];
    const isHorizontal = properties.generalInfo.gridStyle === GRID_STYLES.HORIZONTAL.value;

    headers &&
      headers.map((header: any, index: number) => {
        const renderColumnContent = (dataRow: any) => {
          // get cell data from vertical/horizontal table
          const cell = !isNil(dataRow[header.columnKey]) ? dataRow[header.columnKey] : dataRow[index];
          const cellData = cell && !isNil(cell.data) ? cell.data : cell;

          let displayData = cellData;
          let displayDataTitle = cellData;
          if(AppGlobal.isRenderHtmlInReportViewer()){
            displayData = (<span dangerouslySetInnerHTML={{__html: displayData}}></span>);
            displayDataTitle = ReportPartUtils.getTextContentFromHtmlString(displayDataTitle);
          }

          return (
            <span key={displayDataTitle} title={displayDataTitle} className={(cell && cell.className) || dataRow.className}>
              {displayData}
            </span>
          );
        };

        const getCellStyle = (dataRow: any, isHeader: boolean) => {
          const item = get('style.header', dataRow) || (isHeader ? header.style : header.cellStyle);
          if (!item) return {};
          const {
            fontFamily,
            fontSize,
            bold,
            alignment,
            underline,
            italic,
            textColor,
            backgroundColor,
            wordWrap,
            height
          } = item;
          const result = defaults(DEFAULT_CELL_STYLE, {
            fontFamily: fontFamily,
            fontSize: fontSize + 'px',
            fontWeight: bold ? 'bold' : 'normal',
            textAlign: alignment || 'none',
            textDecoration: underline ? 'underline' : 'none',
            fontStyle: italic ? 'italic' : 'normal',
            wordWrap: wordWrap ? 'break-word' : 'normal',
            whiteSpace: wordWrap ? 'pre-line' : 'pre',
            height: wordWrap ? null : height || null
          });

          if (isHeader) {
            result.color = textColor;
            result.backgroundColor = backgroundColor;
          }

          return result;
        };

        // Get header cell style
        const getHeaderCellStyle = (dataRow: any) => {
          return getCellStyle(dataRow, true);
        };

        // Get content cell style
        const getContentCellStyle = (dataRow: any) => {
          return getCellStyle(dataRow, false);
        };

        // Function to get content style
        const fnGetCellContentStyle = dataRow => {
          if (isHorizontal && index === 0) {
            return defaults(DEFAULT_CELL_STYLE, getHeaderCellStyle(dataRow));
          }

          if ((isHorizontal && index !== 0) || !isHorizontal) {
            return defaults(DEFAULT_CELL_STYLE, getContentCellStyle(dataRow));
          }

          return DEFAULT_CELL_STYLE;
        };

        columns.push(
          <Column
            key={header.columnKey}
            {...header}
            cell={renderColumnContent}
            fnGetCellContentStyle={fnGetCellContentStyle}
            fnGetHeaderCellContentStyle={getHeaderCellStyle}
          />
        );
      });

      return columns;
  }

  /**
   * Parse data formatting
   * @param  {string} dataType      [description]
   * @param  {string} pattern       [description]
   * @param  {Array<any>}  listRecords   [description]
   * @param  {any} fieldsMapping [description]
   * @return {Array<any>}                [description]
   */
  parseDataFormat(dataType: string, pattern: string, listRecords: Array<any>, fieldsMapping: any) {
    const convertFn = dataType === 'Datetime' ? convertToDateTimeFormat : convertToNumeralFormat;
    forEach(record => {
      if (!isNil(record[fieldsMapping])) {
        record[fieldsMapping] = convertFn(pattern, record[fieldsMapping]);
      }
    }, listRecords);

    return listRecords;
  }

  // Generate header data
  generateHeaderData() {
    const {
      reportPart,
      reportPart: { previewDataFieldsMapping, reportPartContent },
      records,
      reportPartElementTypes,
      useFieldNameAlias
    } = this.props;
    const {
      columns,
      properties: { columns: columnSetting, rows: rowSetting }
    } = reportPartContent;
    const headers = [];
    let newRecords = toJS(records);
    const properties = reportPart.getReportPartProperties();
    const { headers: reportPartHeaderProperties } = properties;
    const subTotalRecord = {};
    const maxHeaderHeight = flow(map(get('properties.headerFormating.height')), max)(columns.visibleElement);
    const tableColumnWidth = get('width.value', columnSetting);

    if (!isEmpty(reportPartElementTypes)) {
      forEach(type => { 
        forEach(element => {
          // get field mapping
          const {
            field: { fieldNameAlias },
            properties: elementProperties
          } = element;
          const fieldsMapping = find({ fieldNameAlias: fieldNameAlias }, previewDataFieldsMapping);
          const width = get('properties.dataFormattings.width.value', element);

          if (fieldsMapping) {
            const style = elementProperties.getHeaderCellStyle(reportPartHeaderProperties);
            const cellStyle = elementProperties.getUIStyle();
            style.height = maxHeaderHeight || rowSetting.height;
            cellStyle.height = rowSetting.height;
            headers.push({
              fieldId: fieldsMapping.fieldId,
              columnKey: useFieldNameAlias ? fieldNameAlias : fieldsMapping.columnName,
              title: fieldNameAlias,
              width: width ? width : tableColumnWidth,
              style,
              height: rowSetting.height,
              cellStyle,
              element
            });

            const grandTotal = get('dataFormattings.grandTotal', elementProperties);
            // conver to data format
            if (grandTotal.format.format) {
              newRecords = this.parseDataFormat(
                grandTotal.dataType,
                grandTotal.format.format,
                newRecords,
                fieldsMapping.fieldNameAlias
              );
            }
          }
        }, reportPartContent[type].visibleElement);
      }, reportPartElementTypes);

      // push subtotal into datasource record of grid
      !isEmpty(subTotalRecord) ? newRecords.push(subTotalRecord) : null;
    }

    return {
      headers: headers,
      records: newRecords
    };
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGrandTotalGrid.js